import React, { useMemo } from 'react';
import { Item, RecommendationData } from '../data/RecommendationDataTypes';
import {
    useAdPositionElements,
    insertBanners,
} from '@schibsted-nmp/advertising-companion';
import { useInfiniteFetchMoreData } from '../data/RecommendationDataHooks';
import { i18n } from '@lingui/core';
import Ad from './Ad';
import PulseTracking from '../tracking/pulseTracking';
import { useItemsWithOikotieAdPosition } from './useItemsWithOikotieAdPosition';
import { usePrefetchRecommendationDataQuery } from '../data/RecommendationData';
import { useBottomReached } from '../tracking/bottomReachedTracking';

type AdsProps = {
    cdnBaseUrl: string;
    apiUrl: string;
    loginId: string;
    locale: string;
    position: string;
    favoriteBaseUrl: string;
    recommender: string;
    adId: number;
    numColumns: number;
};

export default function Ads(props: AdsProps) {
    const {
        cdnBaseUrl,
        apiUrl,
        loginId,
        locale,
        position,
        favoriteBaseUrl,
        recommender,
        adId,
        numColumns,
    } = props;

    const [bannerPositions, bannerElements] = useAdPositionElements({
        type: 'recommendation',
        layoutType: 'grid',
    });

    const nodeRef = React.useRef<HTMLDivElement>(null);

    const { recommendationData: initialData, isFetched: initialIsFetched } =
        usePrefetchRecommendationDataQuery();

    const fetchMoreUrls = initialData?.fetchMore;

    const { currentFetchMoreData, isFetched, hasMoreFetchData } =
        useInfiniteFetchMoreData({
            apiUrl,
            ref: nodeRef,
            recommender,
            position,
            fetchMoreUrls,
        });

    const tracking = useMemo(
        () =>
            new PulseTracking({
                position,
                trackingContext: initialData?.['tracking-context'] ?? {},
            }),
        [position, initialData],
    );

    const elements = initialData?.items?.map(
        (item: Item, itemIndex: number) => (
            <Ad
                key={itemIndex}
                item={item}
                cdnBaseUrl={cdnBaseUrl}
                loginId={loginId}
                locale={locale}
                tracking={tracking}
                favoriteBaseUrl={favoriteBaseUrl}
                inscreenTrackingIsEnabled={initialIsFetched}
            />
        ),
    );

    const fetcMoreElements = currentFetchMoreData?.pages?.flatMap(
        (page: RecommendationData) =>
            page?.items?.map((item: Item, itemIndex: number) => (
                <Ad
                    key={itemIndex}
                    item={item}
                    cdnBaseUrl={cdnBaseUrl}
                    loginId={loginId}
                    locale={locale}
                    tracking={tracking}
                    favoriteBaseUrl={favoriteBaseUrl}
                    inscreenTrackingIsEnabled={isFetched}
                />
            )),
    );

    const allElements = elements ? [...elements, ...fetcMoreElements] : [];

    const adItemsWithOikotieBanner = useItemsWithOikotieAdPosition(
        initialIsFetched ? allElements : [],
        position?.includes('front'),
    );

    const adsWithBanners = insertBanners({
        adItems: initialIsFetched ? adItemsWithOikotieBanner : [],
        bannerPositions,
        bannerElements,
    });

    let adsTitle = '';
    if (initialIsFetched) {
        const getAdsTitle = () => {
            if (adId) {
                return i18n.t({
                    id: 'ads.title.mlt',
                    message: 'More like this',
                });
            } else if (initialData?.isPersonal) {
                return i18n.t({
                    id: 'ads.title.personal',
                    message: 'Personalized recommendations',
                });
            } else {
                return i18n.t({
                    id: 'ads.title.popular',
                    message: 'Popular ads',
                });
            }
        };
        if (elements?.length > 0) {
            adsTitle = getAdsTitle();
        }
    }

    const { bottomRef } = useBottomReached({
        onBottomReachedFn: () => {
            if (!hasMoreFetchData) {
                fetch(`${apiUrl}/tracking`);
            }
        },
    });

    return (
        <div>
            <h1 className="text-m">{adsTitle}</h1>
            <div
                className={`grid f-grid grid-flow-row-dense grid-cols-2 ${
                    numColumns === 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-3'
                } gap-10`}
            >
                {adsWithBanners}
                <div ref={nodeRef} />
                <div ref={bottomRef} style={{ height: '1px' }}></div>
            </div>
        </div>
    );
}
